import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Box,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { useToast } from '../../contexts/ToastContext';
import { retailerCatalogApi } from '../../services/retailerCatalog';
import type { Catalogue } from '@traaker/shared';
import { CatalogueIcon, CATALOGUE_ICON_KEYS } from './CatalogueIcon';

/** Edit a catalogue's name / description / icon (cosmetic + descriptive meta). */
export function CatalogueEditDialog({
  open,
  brandId,
  catalogue,
  onClose,
  onSaved,
}: {
  open: boolean;
  brandId: string;
  catalogue: Catalogue;
  onClose: () => void;
  onSaved: (c: Catalogue) => void;
}) {
  const { show } = useToast();
  const [name, setName] = useState(catalogue.name);
  const [description, setDescription] = useState(catalogue.description || '');
  const [icon, setIcon] = useState<string>(catalogue.icon || 'store');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(catalogue.name);
      setDescription(catalogue.description || '');
      setIcon(catalogue.icon || 'store');
    }
  }, [open, catalogue]);

  const save = async () => {
    if (!name.trim()) {
      show('Renseignez le nom du catalogue.', { severity: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const updated = await retailerCatalogApi.updateMeta(brandId, catalogue.catalogueId, {
        name: name.trim(),
        description: description.trim(),
        icon,
      });
      show('Catalogue mis à jour', { severity: 'success' });
      onSaved(updated);
    } catch (e: any) {
      show(e?.message || 'Enregistrement impossible', { severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Modifier le catalogue</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Nom du catalogue"
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label="Description (optionnel)"
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, 500))}
            size="small"
            fullWidth
            multiline
            minRows={2}
            placeholder="Réutilisée comme contexte lors de la création de contenu et de la segmentation."
            helperText={`${description.length}/500`}
          />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Icône du catalogue
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
              {CATALOGUE_ICON_KEYS.map((k) => (
                <IconButton
                  key={k}
                  size="small"
                  onClick={() => setIcon(k)}
                  sx={{
                    border: 1,
                    borderColor: icon === k ? 'primary.main' : 'divider',
                    bgcolor: icon === k ? 'action.selected' : 'transparent',
                    color: icon === k ? 'primary.main' : 'text.secondary',
                  }}
                >
                  <CatalogueIcon icon={k} fontSize="small" />
                </IconButton>
              ))}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Annuler
        </Button>
        <Button variant="contained" onClick={save} disabled={saving || !name.trim()}>
          {saving ? <CircularProgress size={20} /> : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
